import { SxProps, Theme } from "@mui/material";
import {
  IFilterEducationLevels,
  IFilterEducationTypes,
  IFilterGrades,
} from "@src/entities/requestHH/api/interfaces/filtersResponseDto";
import { IStructureVacancyResponseData } from "@src/entities/requestHH/api/interfaces/jobRequestDto";
import {
  TСreateRequestHHDto,
  TСreateRequestHHDto2,
} from "@src/entities/requestHH/api/requests/createRequestHH";
import { mapStructuredVacancyDtoToForm } from "@src/pages/creatingSearchRequestHH/ui/mappers/mapStructuredVacancyDtoToForm";
import * as yup from "yup";

export type TSkillLogic = "and" | "or" | "boolean" | "";
export type TRegions = { id: string; name: string };
export type TSchedule = TRegions;
export type TExperience = IFilterEducationLevels;
export type TEducation = IFilterEducationLevels;
export type TEducationTypes = IFilterEducationTypes;
export type TProfile = { name: string; id: string };
export type TGradeSystem = {
  system_id: string;
  name?: string;
  levels: TProfile[];
};
export type TEditableListItem = {
  value: string;
  uid: string;
};

export type CreatingSearchHHForm = {
  description: string;
  specialization: string;
  skills: string;
  skillLogic: TSkillLogic;
  workType: string[];
  citizenship: boolean;
  workFormat: TSchedule[];
  area: TRegions[];
  salary_from: string;
  salary_to: string;
  showWithout: boolean;
  isRussia: boolean;
  isProfEducation: boolean;
  isFrequentChange: boolean;
  additionalDes: string;
  experience: TExperience[];
  experienceFrom: string;
  experienceTo: string;
  education: TEducationTypes | null;
  isFrequentJobChanges: boolean;
  jobSearchStatus: TEducation[];
  resumeRelocation: TEducation | null;
  ageFrom: string;
  ageTo: string;
  isWithoutAge: boolean;
  profile: TProfile | null;
  specialistLevel: TProfile[];
  gender: string | null;
  isHasCar: boolean | null;
  companyIndustry: string;
  priority: TEditableListItem[];
  optional: TEditableListItem[];
  mandatory: TEditableListItem[];
  exclusionCriteria: TEditableListItem[];
  driverLicenseCategories: string[];
  vacancyName: string;
  isEduOptional: boolean;
};

const defaultValues: CreatingSearchHHForm = {
  description: "",
  specialization: "",
  skills: "",
  skillLogic: "and",
  workType: [],
  isRussia: false,
  area: [],
  salary_from: "",
  salary_to: "",
  showWithout: false,
  citizenship: false,
  workFormat: [],
  isProfEducation: false,
  isFrequentChange: false,
  additionalDes: "",
  experience: [],
  education: null,
  isFrequentJobChanges: false,
  jobSearchStatus: [],
  resumeRelocation: null,
  ageFrom: "",
  ageTo: "",
  isWithoutAge: false,
  profile: null,
  specialistLevel: [],
  gender: "",
  isHasCar: null,
  companyIndustry: "",
  priority: [],
  optional: [],
  mandatory: [],
  exclusionCriteria: [],
  experienceFrom: "",
  experienceTo: "",
  driverLicenseCategories: [],
  vacancyName: "",
  isEduOptional: false,
};

export const createEditableListItem = (value = ""): TEditableListItem => ({
  uid: crypto.randomUUID(),
  value,
});

export const mapStringsToEditableList = (
  values?: string[] | null,
): TEditableListItem[] => {
  if (!values?.length) return [];

  return values
    .map((value) => value.trim())
    .filter(Boolean)
    .filter((value) => value !== "Нет данных.")
    .map((value) => createEditableListItem(value));
};

export const mapEditableListToStrings = (
  values?: TEditableListItem[],
): string[] => {
  if (!values?.length) return [];

  return values.map((item) => item.value.trim()).filter(Boolean);
};

export const normalizeToArray = (
  value?: string[] | string | null,
): string[] => {
  if (!value) return [];

  return Array.isArray(value) ? value : [value];
};

export const mapRequestDtoToForm = (
  params: TСreateRequestHHDto,
  areaOptions: TRegions[],
  experienceOptions: TExperience[],
  educationOptions: TEducationTypes[],
  workFormatOptions: TSchedule[],
  jobStatusOptions: TEducation[],
  resumeRelocationOptions: TEducation[],
  isNewRequest: boolean,
): CreatingSearchHHForm => ({
  description: params.vacancy_description ?? "",
  specialization: params.text ?? "",
  skills: params.key_skills?.join(",") ?? "",
  skillLogic: (params.formated_skills as TSkillLogic)
    ? (params.formated_skills as TSkillLogic)
    : ("and" as TSkillLogic),
  area: params.area?.length
    ? areaOptions.filter(({ name }) => params.area?.includes(name))
    : [],
  salary_from: params.salary_from ? String(params.salary_from) : "",
  salary_to: params.salary_to ? String(params.salary_to) : "",
  showWithout: params.match_without_salary ?? false,
  citizenship: params.citizenship ?? false,
  workFormat: isNewRequest
    ? []
    : workFormatOptions.reduce((acc: Array<TSchedule>, el) => {
        if (params.work_format_ids?.includes(el.id)) {
          acc.push(el);
        }
        return acc;
      }, []) || [],
  experience: isNewRequest
    ? []
    : experienceOptions.filter(({ hh_id }) =>
        params.experience?.includes(hh_id),
      ),
  education: isNewRequest
    ? null
    : (educationOptions.find(({ id }) =>
        params.education_levels?.includes(id),
      ) ?? null),
  isFrequentJobChanges: params.exclude_jumpers ?? false,
  jobSearchStatus: isNewRequest
    ? []
    : jobStatusOptions.filter(({ hh_id }) =>
        params.job_search_status?.includes(hh_id),
      ),
  resumeRelocation: isNewRequest
    ? null
    : resumeRelocationOptions.find(({ hh_id }) =>
        params.resume_search_relocation?.includes(hh_id),
      ) || null,
  workType: [],
  isRussia: params.area?.length ? params.area?.includes("Россия") : false,
  isProfEducation: false,
  isFrequentChange: false,
  additionalDes: "",
  ageFrom: params?.age_from ?? "",
  ageTo: params?.age_to ?? "",
  exclusionCriteria: mapStringsToEditableList(
    normalizeToArray(params.exclusion_criteria),
  ),
  isWithoutAge: params?.match_without_age ?? false,
  //Доработать когда будет юэк
  profile: null,
  specialistLevel: [],
  gender: "",
  isHasCar: params?.requires_car,
  companyIndustry: "",
  priority: mapStringsToEditableList(params?.required_skills),
  optional: mapStringsToEditableList(params?.nice_to_have_skills),
  mandatory: mapStringsToEditableList(params?.must_have_skills),
  experienceFrom: "",
  experienceTo: "",
  driverLicenseCategories: [],
  vacancyName: params?.vacancy_name || "",
  isEduOptional: params?.is_edu_optional ?? false,
});

export const isNewRequestHHParams = (
  params: TСreateRequestHHDto | TСreateRequestHHDto2 | null | undefined,
): params is TСreateRequestHHDto2 => {
  if (!params || typeof params !== "object") {
    return false;
  }

  return (
    "requirements" in params &&
    Boolean(params.requirements) &&
    typeof params.requirements === "object"
  );
};

export const getDefaultValues = (
  isNewRequest: boolean,
  areaOptions: TRegions[],
  experienceOptions: TExperience[],
  educationOptions: TEducationTypes[],
  workFormatOptions: TSchedule[],
  jobStatusOptions: TEducation[],
  resumeRelocationOptions: TEducation[],
  gradeOptions: IFilterGrades,
  params?: TСreateRequestHHDto | TСreateRequestHHDto2 | null,
): CreatingSearchHHForm => {
  if (isNewRequest || !params) {
    return defaultValues;
  }

  if (isNewRequestHHParams(params)) {
    return mapStructuredVacancyDtoToForm({
      baseValues: defaultValues,
      structuredVacancy: params as IStructureVacancyResponseData,
      areaOptions,
      experienceOptions,
      educationOptions,
      workFormatOptions,
      jobStatusOptions,
      resumeRelocationOptions,
      gradeOptions,
    });
  }

  return {
    ...mapRequestDtoToForm(
      params,
      areaOptions,
      experienceOptions,
      educationOptions,
      workFormatOptions,
      jobStatusOptions,
      resumeRelocationOptions,
      isNewRequest,
    ),
  };
};

const parseOptionalNumber = (value?: string | null): number | null => {
  if (value == null) return null;

  const normalized = String(value).trim();

  if (!normalized) return null;

  const parsed = Number(normalized);

  return Number.isNaN(parsed) ? null : parsed;
};

export const creatingSearchHHSchema = yup.object({
  mandatory: yup
    .array()
    .of(
      yup.object({
        uid: yup.string().required(),
        value: yup.string().trim(),
      }),
    )
    .test(
      "has-value",
      "Добавьте хотя бы одно значение",
      (value) => value?.some((item) => item.value?.trim()) ?? false,
    ),
  salary_from: yup
    .string()
    .test(
      "salary-from-less-than-to",
      "Зарплата «От» не может быть больше зарплаты «До»",
      function (value) {
        const { salary_to } = this.parent;

        if (!value || !salary_to) return true;

        return Number(value) <= Number(salary_to);
      },
    ),

  salary_to: yup
    .string()
    .test(
      "salary-to-more-than-from",
      "Зарплата «До» не может быть меньше зарплаты «От»",
      function (value) {
        const { salary_from } = this.parent;

        if (!value || !salary_from) return true;

        return Number(value) >= Number(salary_from);
      },
    ),

  ageFrom: yup
    .string()
    .test("age-from-max", "Возраст не может быть больше 120", (value) => {
      const from = parseOptionalNumber(value);

      if (from === null) return true;

      return from <= 120;
    })
    .test(
      "age-from-less-than-to",
      "Возраст «От» не может быть больше возраста «До»",
      function (value) {
        const from = parseOptionalNumber(value);
        const to = parseOptionalNumber(this.parent.ageTo);

        if (from === null || to === null) return true;

        return from <= to;
      },
    ),

  ageTo: yup
    .string()
    .test("age-to-max", "Возраст не может быть больше 120", (value) => {
      const to = parseOptionalNumber(value);

      if (to === null) return true;

      return to <= 120;
    })
    .test(
      "age-to-greater-than-from",
      "Возраст «До» не может быть меньше возраста «От»",
      function (value) {
        const from = parseOptionalNumber(this.parent.ageFrom);
        const to = parseOptionalNumber(value);

        if (from === null || to === null) return true;

        return to >= from;
      },
    ),
});

export type UpdateUserFormValues = yup.InferType<typeof creatingSearchHHSchema>;

export const textareaStyles: SxProps<Theme> = {
  width: "100%",
  backgroundColor: "none",
  color: "rgba(164, 187, 199, 1)",
  marginBottom: "35px",

  "& .MuiOutlinedInput-input::placeholder": {
    color: "#9aa0a6",
    opacity: 1,
  },

  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    padding: "0px",
    color: "red",

    "& fieldset": {
      borderColor: "rgba(164, 187, 199, 0.5)",
      borderRadius: "12px",
    },

    "&:hover fieldset": {
      borderColor: "rgba(164, 187, 199, 0.5)",
    },

    "&.Mui-focused fieldset": {
      borderColor: "rgba(164, 187, 199, 0.5)",
    },

    "& textarea": {
      padding: "16px",
    },
  },
};

export const textareaRightBlockStyles: SxProps<Theme> = {
  width: "100%",
  height: "100%",
  backgroundColor: "none",
  flexGrow: "1",
  color: "rgba(255, 255, 255, 1)",

  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    padding: "0px",
    height: "100%",

    "& fieldset": {
      borderColor: "#ffffff",
      borderTopRightRadius: "12px",
      borderBottomLeftRadius: "12px",
      borderBottomRightRadius: "12px",
      borderTopLeftRadius: "12px",
    },

    "&:hover fieldset": {
      borderColor: "#ffffffe0",
    },

    "&.Mui-focused fieldset": {
      borderColor: "#ffffffe0",
    },

    "& textarea": {
      padding: "16px",
      height: "100%",
    },
  },
};

export const radioStyles = {
  color: "rgba(164, 187, 199, 0.25)",
  "&.Mui-checked": {
    color: "#fff",
  },
  "& .MuiSvgIcon-root": {
    fontSize: 22,
  },
};

export const getAreaId = (
  isAllArea: boolean,
  allRegionsId: string,
  area: TRegions[] = [],
) => {
  if (isAllArea) {
    return [allRegionsId];
  }
  return area.length ? area.map((region) => region.id) : [];
};

export const ALL_REGIONS_ID = "113";
export const LIVING_ID = "living";

export const getNumericalValue = (value: string) => {
  if (!value) {
    return null;
  }
  const numericalValue = Number(value);

  if (numericalValue === 0) {
    return null;
  }

  return numericalValue;
};
